import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent, useEffect } from 'react'
import { AlertCircle } from 'lucide-react'

interface Community {
  id: number
  name: string
}

interface Answer {
  text: string
}

interface QuizData {
  id?: number
  title: string
  question: string
  answers: Answer[]
  correct_answer_indices: number[]
  category: string
  difficulty_level: number
  points_reward: number
  week_start_date: string
  published: boolean
  community_id: number | string
}

interface QuizFormProps {
  quiz?: QuizData
  communities: Community[]
  suggestedStartDate?: string
  onSubmit: string
  isEdit?: boolean
}

export function QuizForm({
  quiz,
  communities,
  suggestedStartDate,
  onSubmit,
  isEdit = false
}: QuizFormProps) {
  const { data, setData, post, put, processing, errors } = useForm({
    title: quiz?.title || '',
    question: quiz?.question || '',
    answers: quiz?.answers || [
      { text: '' },
      { text: '' },
      { text: '' },
      { text: '' }
    ],
    correct_answer_indices: quiz?.correct_answer_indices || [],
    category: quiz?.category || 'sav',
    difficulty_level: quiz?.difficulty_level || 1,
    points_reward: quiz?.points_reward || 10,
    week_start_date: quiz?.week_start_date || suggestedStartDate || '',
    published: quiz?.published || false,
    community_id: quiz?.community_id?.toString() || ''
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    // Validation
    if (data.correct_answer_indices.length === 0) {
      alert('Veuillez sélectionner au moins une bonne réponse')
      return
    }

    const hasEmptyAnswers = data.answers.some(a => !a.text.trim())
    if (hasEmptyAnswers) {
      alert('Toutes les réponses doivent être remplies')
      return
    }

    if (isEdit && quiz?.id) {
      put(onSubmit)
    } else {
      post(onSubmit)
    }
  }

  const updateAnswer = (index: number, text: string) => {
    const newAnswers = [...data.answers]
    newAnswers[index] = { text }
    setData('answers', newAnswers)
  }

  const weekEndDate = data.week_start_date
    ? new Date(new Date(data.week_start_date).getTime() + 6 * 24 * 60 * 60 * 1000)
        .toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : ''

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6">
        <div className="space-y-8">
          {/* Section 1: Informations Générales */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Informations Générales</h3>
            <div className="space-y-4">
              {/* Titre */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-text-dark mb-1">
                  Titre *
                </label>
                <Input
                  id="title"
                  type="text"
                  maxLength={200}
                  value={data.title}
                  onChange={(e) => setData('title', e.target.value)}
                  placeholder="Ex: Quiz SAV - Semaine du 3 février"
                  required
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
                <p className="mt-1 text-xs text-text-gray">{data.title.length}/200 caractères</p>
              </div>

              {/* Communauté */}
              <div>
                <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
                  Communauté *
                </label>
                <Select
                  id="community_id"
                  value={data.community_id}
                  onChange={(e) => setData('community_id', e.target.value)}
                  required
                >
                  <option value="">Sélectionner une communauté</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.name}
                    </option>
                  ))}
                </Select>
                {errors.community_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.community_id}</p>
                )}
              </div>

            </div>
          </div>

          {/* Section 2: Question et Réponses */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Question et Réponses</h3>
            <div className="space-y-4">
              {/* Question */}
              <div>
                <label htmlFor="question" className="block text-sm font-medium text-text-dark mb-1">
                  Question *
                </label>
                <Textarea
                  id="question"
                  value={data.question}
                  onChange={(e) => setData('question', e.target.value)}
                  placeholder="Quel est le bon réflexe si la cabine affiche réservé ?"
                  rows={4}
                  required
                />
                {errors.question && (
                  <p className="mt-1 text-sm text-red-600">{errors.question}</p>
                )}
              </div>

              {/* Réponses */}
              {[0, 1, 2, 3].map((index) => (
                <div key={index}>
                  <label htmlFor={`answer_${index}`} className="block text-sm font-medium text-text-dark mb-1">
                    Réponse {index + 1} *
                  </label>
                  <div className="flex gap-2 items-start">
                    <Input
                      id={`answer_${index}`}
                      type="text"
                      value={data.answers[index]?.text || ''}
                      onChange={(e) => updateAnswer(index, e.target.value)}
                      placeholder={`Réponse ${index + 1}`}
                      required
                      className="flex-1"
                    />
                    <label className={`flex items-center gap-2 px-4 py-2 rounded border cursor-pointer transition-colors ${
                      data.correct_answer_indices.includes(index)
                        ? 'bg-green-50 border-green-300'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}>
                      <input
                        type="checkbox"
                        checked={data.correct_answer_indices.includes(index)}
                        onChange={() => {
                          const indices = data.correct_answer_indices.includes(index)
                            ? data.correct_answer_indices.filter(i => i !== index)
                            : [...data.correct_answer_indices, index]
                          setData('correct_answer_indices', indices)
                        }}
                        className="text-tessan-primary"
                      />
                      <span className="text-sm text-text-dark whitespace-nowrap">Bonne réponse</span>
                    </label>
                  </div>
                </div>
              ))}

              {data.correct_answer_indices.length === 0 && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <AlertCircle size={18} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-yellow-800">
                    Veuillez sélectionner au moins une bonne réponse en cochant les cases à droite.
                    Vous pouvez en sélectionner plusieurs pour un quiz à choix multiples.
                  </p>
                </div>
              )}

              {data.correct_answer_indices.length > 1 && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <AlertCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    Quiz à choix multiples : {data.correct_answer_indices.length} bonnes réponses sélectionnées.
                    L'utilisateur devra cocher toutes les bonnes réponses pour obtenir les points.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Planification */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Planification</h3>
            <div className="space-y-4">
              {/* Date de début */}
              <div>
                <label htmlFor="week_start_date" className="block text-sm font-medium text-text-dark mb-1">
                  Date de début *
                </label>
                <Input
                  id="week_start_date"
                  type="date"
                  value={data.week_start_date}
                  onChange={(e) => setData('week_start_date', e.target.value)}
                  required
                />
                {errors.week_start_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.week_start_date}</p>
                )}
                <p className="mt-1 text-xs text-text-gray">
                  Le quiz sera visible à partir de ce jour
                </p>
              </div>

              {/* Date de fin (calculée) */}
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  Date de fin
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-text-gray">
                  {weekEndDate || 'Sélectionnez d\'abord une date de début'}
                </div>
                <p className="mt-1 text-xs text-text-gray">
                  Automatiquement calculé (7 jours après le début)
                </p>
              </div>
            </div>
          </div>

          {/* Section 4: Points */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Points</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="points_reward" className="block text-sm font-medium text-text-dark mb-1">
                  Points pour bonne réponse
                </label>
                <Input
                  id="points_reward"
                  type="number"
                  min={1}
                  max={100}
                  value={data.points_reward}
                  onChange={(e) => setData('points_reward', parseInt(e.target.value) || 10)}
                />
                {errors.points_reward && (
                  <p className="mt-1 text-sm text-red-600">{errors.points_reward}</p>
                )}
                <p className="mt-1 text-xs text-text-gray">
                  Bonus de +10 points automatique au 3e bonne réponse consécutive
                </p>
              </div>
            </div>
          </div>

          {/* Section 5: Publication */}
          <div>
            <h3 className="text-lg font-semibold text-text-dark mb-4">Publication</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.published}
                onChange={(e) => setData('published', e.target.checked)}
                className="mt-1 text-tessan-primary"
              />
              <div>
                <p className="font-medium text-text-dark">Publier immédiatement</p>
                <p className="text-sm text-text-gray">
                  Si coché, le quiz sera visible par les utilisateurs. Sinon, il sera sauvegardé en brouillon.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={() => history.back()}>
            Annuler
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Sauvegarder'}
          </Button>
        </div>
      </Card>
    </form>
  )
}
